'use client';

import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import classNames from 'classnames';

import styles from './FinishOnPhone.module.css';

import logError from '@/helpers/logError';

import { HeadingText } from '@/components/atoms/HeadingText';
import { HeadingDescription } from '@/components/atoms/HeadingDescription';
import { QRCode } from '@/components/QRCode';
import { useFormContext } from '@/components/forms/FormContext';
import { Button } from '@/components/atoms/Button';

export interface FinishOnPhoneProps {
	testId?: string;
	setShowQR: Dispatch<SetStateAction<boolean>>;
	setShowUpload: Dispatch<SetStateAction<boolean>>;
}

const FinishOnPhone = (props: FinishOnPhoneProps) => {
	const { testId = 'FinishOnPhone', setShowUpload, setShowQR } = props;
	const { state } = useFormContext();
	const [url, setUrl] = useState('');

	useEffect(() => {
		const getUrl = async () => {
			try {
				const response = await fetch('/api/store/', {
					method: 'POST',
					body: JSON.stringify(state),
				});

				const storeId = await response.text();

				if (!storeId) {
					logError(new Error('Failed to get Store ID'));
				}

				const url = new URL(
					`${window.location.origin}${window.location.pathname}?q=${storeId}`,
				).toString();

				return url;
			} catch (error) {
				logError(error as Error);
			}
		};

		getUrl().then((url) => {
			if (url) setUrl(url);
		});
	}, [state]);

	function changeToDesktop() {
		setShowQR(false);
		setShowUpload(true);
	}

	return (
		<div data-testid={testId}>
			<HeadingText tag="h1" size="h3" className="mb-3 lg:mb-4" balanced>
				Take a photo with your phone
			</HeadingText>

			<HeadingDescription className="text-xl !text-black lg:!text-2xl">
				Scan the QR code below to easily switch to your phone to take or upload
				a photo.
			</HeadingDescription>

			<div className="mb-10 inline-flex flex-col items-center gap-6 lg:flex-row">
				<div className="relative h-45 w-45 p-5">
					<figure className={classNames(styles['rainbow-border'], 'h-35 w-35')}>
						<QRCode data={url} alt="QR Code" width={140} height={140} />
					</figure>
				</div>

				<ol
					className="ml-6 inline-flex w-auto list-decimal flex-col gap-2 text-left text-sm
							text-slate-500 lg:text-xl"
				>
					<li>Point your phone&apos;s camera app at this code</li>
					<li>Tap the link that pops up</li>
					<li>Take or upload pics from your phone</li>
					<li>Complete the steps on your mobile device</li>
				</ol>
			</div>

			<div className="lg:mb-6">
				<Button
					variant="large-default"
					className="mx-auto w-full max-w-101"
					onClick={changeToDesktop}
				>
					Nevermind, go back
				</Button>
			</div>
		</div>
	);
};

export default FinishOnPhone;
